import { useCallback, useEffect, useRef, useState } from 'react';
import { PRODUCT_NAME } from '@goanna-im/shared';
import type { InfoMessage } from '@goanna-im/shared';
import { useSession } from '../lib/useSession.js';
import { keyToIntent } from '../lib/keymap.js';
import { useGamepad } from '../lib/useGamepad.js';
import { GAMEPAD_RECALL_BUTTON, gamepadButtonToScroll } from '../lib/gamepadMap.js';
import { useVoiceFollow } from '../lib/useVoiceFollow.js';
import { useTranscript } from '../lib/useTranscript.js';
import {
  readBatteryPct,
  releaseWakeLock,
  requestWakeLock,
  toggleFullscreen,
} from '../lib/capabilities.js';
import { ConnectionBadge } from '../components/ConnectionBadge.js';
import { RoomCode } from '../components/RoomCode.js';
import { InfoZone } from './InfoZone.js';
import { ScriptStage } from './ScriptStage.js';

export function PresenterRoute() {
  const { state, actions } = useSession();

  if (!state.joined || !state.session) {
    return <PresenterSetup onJoin={actions.joinSession} error={state.error} />;
  }

  return <PresenterStage state={state} actions={actions} />;
}

function PresenterSetup({
  onJoin,
  error,
}: {
  onJoin: (code: string, role: 'presenter', name: string) => void;
  error: string | null;
}) {
  const roomParam = (new URLSearchParams(window.location.search).get('room') ?? '').toUpperCase();
  const [code, setCode] = useState(roomParam);
  const autoTried = useRef(false);

  // Scanning the producer's QR lands here with ?room=CODE → auto-join, no typing.
  useEffect(() => {
    if (roomParam.length >= 4 && !autoTried.current) {
      autoTried.current = true;
      onJoin(roomParam, 'presenter', 'Presenter');
    }
  }, [roomParam, onJoin]);

  return (
    <div className="wrap stack" style={{ paddingTop: '3rem', maxWidth: 520 }}>
      <h1 className="brand" style={{ margin: 0 }}>
        {PRODUCT_NAME}
        <small>Presenter display</small>
      </h1>
      <p className="muted">
        Place this device under the teleprompter glass. Enter the room code from your producer, or
        scan their QR.
      </p>
      <div className="card stack">
        <div className="row">
          <input
            aria-label="Room code"
            placeholder="Room code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            type="text"
            name="roomcode"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            style={{ flex: 1 }}
          />
          <button className="primary" disabled={code.length < 3} onClick={() => onJoin(code, 'presenter', 'Presenter')}>
            Join
          </button>
        </div>
        {error && <p style={{ color: 'var(--urgent)' }}>{error}</p>}
      </div>
    </div>
  );
}

function PresenterStage({
  state,
  actions,
}: {
  state: ReturnType<typeof useSession>['state'];
  actions: ReturnType<typeof useSession>['actions'];
}) {
  const session = state.session!;
  const { settings, layout, message, scroll, project, presence, roomCode } = session;
  const [showControls, setShowControls] = useState(true);
  const [lastKey, setLastKey] = useState('');
  const [lastBtn, setLastBtn] = useState<number | null>(null);
  const [recall, setRecall] = useState<InfoMessage | null>(null);
  const lastMsgRef = useRef<InfoMessage | null>(null);
  const recallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Menu button re-shows the most recent producer message on the presenter for 8s.
  const recallLastMessage = useCallback(() => {
    if (!lastMsgRef.current) return;
    // Drop any (possibly past) expiry so the recalled message actually displays;
    // our own 8s timer clears it.
    setRecall({ ...lastMsgRef.current, expiresAt: undefined });
    if (recallTimer.current) clearTimeout(recallTimer.current);
    recallTimer.current = setTimeout(() => setRecall(null), 8000);
  }, []);

  // Game controller (Gamepad API) — buttons only (this remote has no analog axes).
  const gp = useGamepad(
    (index) => {
      setLastBtn(index);
      if (index === GAMEPAD_RECALL_BUTTON) {
        recallLastMessage();
        return;
      }
      const control = gamepadButtonToScroll(index);
      if (control) actions.scroll(control);
    },
    () => {
      /* no analog stick on this remote */
    },
  );
  const stageRef = useRef<HTMLDivElement>(null);
  const hasProducer = presence.some((p) => p.role === 'producer');
  const vf = useVoiceFollow(project, actions.scroll, scroll.anchor.blockId);
  // Capture the show transcript when the producer has recording on. Paused while
  // voice-follow drives the mic, to avoid two recognisers fighting for it.
  const tx = useTranscript(session.recording && !vf.active, actions.sendTranscript);

  // Remember the most recent producer message so the Menu button can recall it.
  useEffect(() => {
    if (message) lastMsgRef.current = message;
  }, [message]);

  // The message actually on screen: a Menu-recalled one takes precedence briefly.
  const shown = recall ?? message;

  // Info zone grows for cues (esp. urgent), then auto-returns to the layout ratio.
  let infoPct = layout.infoRatio * 100;
  if (shown) infoPct = Math.max(infoPct, shown.type === 'urgent' ? 62 : 26);

  // Keep the screen awake while presenting; re-acquire when returning to the tab.
  useEffect(() => {
    void requestWakeLock();
    const onVis = () => {
      if (document.visibilityState === 'visible') void requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      void releaseWakeLock();
    };
  }, []);

  // Report battery once (capability-detected; silently skipped when unavailable).
  useEffect(() => {
    void readBatteryPct().then((batteryPct) => {
      if (batteryPct !== undefined) actions.updatePresence({ batteryPct, online: true });
    });
  }, [actions]);

  // Keyboard + Bluetooth-remote control.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Capture EVERY key (incl. unmapped) so we can identify a Bluetooth remote's buttons.
      setLastKey(`key="${e.key}"  code="${e.code}"  keyCode=${e.keyCode}`);
      const intent = keyToIntent(e);
      if (!intent) return;
      e.preventDefault();
      if (intent.kind === 'scroll') actions.scroll(intent.control);
      else if (intent.kind === 'toggleControls') setShowControls((v) => !v);
      else if (intent.kind === 'exit') setShowControls((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const mirrorClass = settings.mirrorHorizontal && settings.mirrorVertical
    ? 'mirror-hv'
    : settings.mirrorHorizontal
      ? 'mirror-h'
      : settings.mirrorVertical
        ? 'mirror-v'
        : '';

  const reanchor = useCallback((r: { blockId: string; offsetFraction: number }) => actions.reanchor(r), [actions]);

  return (
    <>
      <div className={`stage ${mirrorClass}`} ref={stageRef}>
        <InfoZone message={shown} heightPct={infoPct} />
        <ScriptStage project={project} scroll={scroll} settings={settings} onReanchor={reanchor} />
      </div>

      <div className="status-pill">
        <ConnectionBadge connected={state.connected} presence={presence} />
      </div>

      {session.recording && (
        <div
          className="status-pill"
          style={{ right: 'auto', left: '0.6rem', color: '#ef4444', fontWeight: 700 }}
          title={tx.supported ? 'This show is being recorded (transcript)' : 'Recording on, but this browser has no speech support'}
        >
          ● REC{tx.supported ? '' : ' (no mic)'}
        </div>
      )}

      {!hasProducer && (
        <div className="controls-overlay" role="region" aria-label="Pairing">
          <div className="wrap stack">
            <RoomCode roomCode={roomCode} />
          </div>
        </div>
      )}

      {showControls && (
        <div className="controls-overlay" role="region" aria-label="Presenter controls">
          <div className="wrap row" style={{ justifyContent: 'space-between' }}>
            <div className="row">
              <button onClick={() => actions.updateSettings({ mirrorHorizontal: !settings.mirrorHorizontal })}>
                Mirror H: {settings.mirrorHorizontal ? 'on' : 'off'}
              </button>
              <button onClick={() => actions.updateSettings({ mirrorVertical: !settings.mirrorVertical })}>
                Mirror V: {settings.mirrorVertical ? 'on' : 'off'}
              </button>
              <button onClick={() => toggleFullscreen(document.documentElement)}>Fullscreen</button>
              {vf.supported && (
                <button className={vf.active ? 'danger' : ''} onClick={vf.toggle}>
                  {vf.active ? '■ Voice-follow' : '🎤 Voice-follow'}
                </button>
              )}
            </div>
            <div className="row">
              <span className="tag readout">
                {Math.round(scroll.speedPxPerSec)} px/s · {scroll.running ? 'running' : 'paused'}
              </span>
              <button onClick={() => setShowControls(false)}>Hide controls (C)</button>
            </div>
          </div>
          <div className="wrap stack" style={{ paddingTop: '0.4rem', gap: '0.3rem' }}>
            <span className="tag readout">Key: {lastKey || '— (keyboard mode) —'}</span>
            <span className="tag readout">
              🎮 {gp.connected ? gp.id.slice(0, 44) : 'no gamepad — switch controller to GAME mode'}
            </span>
            {gp.connected && (
              <span className="tag readout">
                pressed [{gp.pressed.join(',') || '—'}] · last btn {lastBtn ?? '—'} · axes [{gp.axes.join(', ')}]
              </span>
            )}
            {vf.active && (
              <span className="tag readout">
                🎤 {vf.status} · {vf.trackingBlock ? `block ${vf.trackingBlock}/${vf.totalBlocks}` : 'listening'} ·
                heard: {vf.heard || '—'}
              </span>
            )}
          </div>
        </div>
      )}

      {!showControls && (
        <button
          className="status-pill"
          style={{ top: 'auto', bottom: '0.6rem', right: '0.6rem' }}
          onClick={() => setShowControls(true)}
        >
          Controls
        </button>
      )}
    </>
  );
}
